// attachReceiptToTrip — post-commit receipt attachment.
//
// At commit time, CheckoutScreen + commitTrip together compress a
// photo, run scan-receipt OCR, write a `receipts` row, upload the
// image, and link `shopping_trips.receipt_id`. If the user skips the
// photo at checkout, the trip ships with `receipt_id = null` and
// there's no recovery path. This helper IS that recovery path —
// extracted from the commit-time flow so TripHistoryModal can call it
// after the fact without duplicating logic.
//
// One round of OCR is fine here for the same reason it's fine at
// commit: the user has explicitly tapped "attach receipt," so the
// LLM call is opt-in by the user's act of choosing to attach.
//
// Inputs:
//   file       — File from <input type="file"> or a Blob
//   tripId     — shopping_trips.id to link to
//   userId     — owner; lands on receipts.user_id and the storage path
//   supabase   — supabase-js client (passed in to keep this React-free)
//   itemCount  — optional; rough count for the receipts.item_count
//                column. Falls back to null when caller doesn't know.
//
// Returns:
//   { ok: true,  receiptId, ocrApplied }
//   { ok: false, error }
//
// All writes are best-effort beyond the initial receipts insert. If
// OCR fails, we still attach the photo. If the storage upload fails,
// we still keep the row (caller can retry).

import { compressImage } from "./compressImage";

export async function attachReceiptToTrip({
  file,
  tripId,
  userId,
  supabase,
  itemCount = null,
}) {
  if (!file)     return { ok: false, error: "no file provided" };
  if (!tripId)   return { ok: false, error: "no tripId provided" };
  if (!userId)   return { ok: false, error: "no userId provided" };
  if (!supabase) return { ok: false, error: "no supabase client" };

  // 1. Compress. Match CheckoutScreen's params (1100px / 0.8) so the
  //    same OCR quality contract holds.
  let base64, mediaType;
  try {
    const out = await compressImage(file, { maxDimension: 1100, jpegQuality: 0.8 });
    base64 = out.base64;
    mediaType = out.mediaType;
  } catch (e) {
    return { ok: false, error: `image compression failed: ${e?.message || e}` };
  }

  // 2. Best-effort OCR. Failure is fine — we still want the photo
  //    attached. The receipts row absorbs nulls if the parse misses.
  let parsedStore = null, parsedDate = null, parsedTotalCents = null;
  let ocrApplied = false;
  try {
    const { data, error: fnErr } = await supabase.functions.invoke("scan-receipt", {
      body: { image: base64, mediaType, ingredients: [] },
    });
    if (!fnErr && data && !data.error) {
      parsedStore      = data.store ?? null;
      parsedDate       = typeof data.date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(data.date) ? data.date : null;
      parsedTotalCents = typeof data.totalCents === "number" ? data.totalCents : null;
      ocrApplied = true;
    }
  } catch (e) {
    console.warn("[attach-receipt] OCR failed (proceeding with photo only):", e?.message || e);
  }

  // 3. Insert receipts row.
  let receiptId = null;
  try {
    const { data, error } = await supabase
      .from("receipts")
      .insert({
        user_id:      userId,
        store_name:   parsedStore,
        receipt_date: parsedDate,
        total_cents:  parsedTotalCents,
        item_count:   itemCount,
      })
      .select("id")
      .single();
    if (error) throw error;
    receiptId = data?.id || null;
  } catch (e) {
    return { ok: false, error: `receipts insert failed: ${e?.message || e}` };
  }

  // 4. Upload image to the scans bucket. Best-effort — the row exists
  //    even if upload fails; user can retry attach.
  if (receiptId) {
    try {
      const ext = (mediaType || "image/jpeg").split("/")[1]?.split(";")[0] || "jpg";
      const path = `${userId}/${receiptId}.${ext}`;
      const bin = atob(base64);
      const bytes = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
      const blob = new Blob([bytes], { type: mediaType });
      // remove() before upload() — see feedback memory: upsert leaves
      // stale cache-control / etag on existing objects.
      try { await supabase.storage.from("scans").remove([path]); } catch { /* ignore */ }
      const { error: upErr } = await supabase.storage
        .from("scans")
        .upload(path, blob, { contentType: mediaType, upsert: true });
      if (!upErr) {
        await supabase.from("receipts").update({ image_path: path }).eq("id", receiptId);
      } else {
        console.warn("[attach-receipt] image upload failed:", upErr.message);
      }
    } catch (e) {
      console.warn("[attach-receipt] image upload threw:", e?.message || e);
    }
  }

  // 5. Link the trip.
  try {
    const update = { receipt_id: receiptId };
    if (parsedStore) update.store_name = parsedStore;
    const { error: tripErr } = await supabase
      .from("shopping_trips")
      .update(update)
      .eq("id", tripId);
    if (tripErr) throw tripErr;
  } catch (e) {
    return { ok: false, error: `trip-link update failed: ${e?.message || e}`, receiptId };
  }

  return { ok: true, receiptId, ocrApplied };
}
